import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {classToClass, plainToClass} from "class-transformer";
import {SolutionManagerDataService} from "@app/solution-manager/solution-manager-data.service";
import {UserService} from "@app/core";
import {SelectItem} from "primeng/api";
import {forkJoin} from "rxjs";
import {DefaultPermissionSetting, FilePermission, FilePermissionSetting, FilePermissionSettingType, FilePermissionType, FileSecurityRoleConfig} from '@app/shared/classes/doc-file';
import {DefaultOrg} from "@app/shared/enums/default-org.enum";
import {EnumType} from "@app/shared/enums/enum-type.enum";
import {CommonDataService} from '@app/shared/services/common-data.service';
import {CommonDocRepoService} from '@app/shared/services/common-doc-repo.service';
import {PrimeNgConfig, TableColumn, TableFormat} from "@app/shared";

@Component({
  selector: 'app-file-permission-setting',
  templateUrl: './file-permission-setting.component.html',
  styleUrls: ['./file-permission-setting.component.css'],
  providers: [SolutionManagerDataService, CommonDocRepoService]
})
export class FilePermissionSettingComponent implements OnInit, AfterViewInit {

  DefaultOrg: typeof DefaultOrg = DefaultOrg;
  FilePermissionSettingType: typeof FilePermissionSettingType = FilePermissionSettingType;
  PrimeNgConfig = PrimeNgConfig;

  //Default Settings
  @Input() defaultSettingsForSelectedTxn: DefaultPermissionSetting[] = [];
  roleConfigForSelectedTxn: FileSecurityRoleConfig[] = [];
  @Input() settingType: FilePermissionSettingType;
  filePermissions: FilePermission[] = [];
  defaultPermissionMap = new Map<string, DefaultPermissionSetting>();
  defaultSettingsFromBackEnd: DefaultPermissionSetting[] = [];
  roles: SelectItem[] = [];
  selectedRoles: string[] = [];
  @Input() transaction: string;
  displayDialog: boolean = true;
  @Output() cancelEvent = new EventEmitter();
  @Output() savePermissionEvent: EventEmitter<DefaultPermissionSetting[] | FilePermissionSetting[]> = new EventEmitter();
  roleTableColumns: TableColumn[];                                // column headers and fields to display on the table
  actionTableColumns: TableColumn[];
  roleConfigTableColumns: TableColumn[];
  selectedRole: string;
  //TODO: Typecast to roles
  TXN_APPROVER: string = "AW_TXN_APPROVER";
  FILE_OWNER: string = "AW_FILE_OWNER";

  //File Access Setting properties
  existingFileSettings: FilePermissionSetting[] = [];
  filePermissionMap = new Map<string, FilePermissionSetting>();
  @Input() selectedDocFileId: number;

  @ViewChild('actionTemplate') actionTemplate: TemplateRef<any>;

  constructor(private dataService: SolutionManagerDataService, public user: UserService, private commonDataService: CommonDataService,
              private docService: CommonDocRepoService) {
  }

  ngOnInit() {
    this.getFileSettings();
    this.getRoles();
  }

  ngAfterViewInit() {
    this.initTableColumns();
  }

  onChangeRole(): void {
    this.filePermissions = [];
    //For each selected role in the multi-select, form corresponding filePermission
    this.selectedRoles.forEach(role => {
      if (this.isDefaultSettings() && this.defaultPermissionMap.has(role)) {
        this.filePermissions.push(this.defaultPermissionMap.get(role).permission);
      } else if (this.isFileSettings() && this.filePermissionMap.has(role)) {
        this.filePermissions.push(this.filePermissionMap.get(role).permission);
      } else {
        let filePermission = new FilePermission();
        filePermission.role = role;
        filePermission.canRead = false;
        filePermission.canWrite = false;
        filePermission.canDelete = false;
        filePermission.canPermit = false;
        //TODO: Handle Owner and approver behaviour
        if (role == this.FILE_OWNER) {
          filePermission.filePermissionType = FilePermissionType.Owner;
        } else if (role == this.TXN_APPROVER) {
          filePermission.filePermissionType = FilePermissionType.Approver;
        } else {
          filePermission.filePermissionType = FilePermissionType.Role;
        }
        this.filePermissions.push(filePermission);
      }
    });
  }

  onClickCloseDialog(): void {
    this.cancelEvent.emit();
  }

  onClickSavePermissions(): void {
    if (this.isDefaultSettings()) {
      this.saveDefaultSettings();
    } else if (this.isFileSettings()) {
      this.saveFileSettings();
    }
  }

  /**
   * Remove the deleted permission from permissions and selected Options in multi select
   */
  onClickDeletePermission(per: FilePermission): void {
    const index = this.filePermissions.findIndex(permission => permission.role == per.role);
    if (index >= 0) {
      this.filePermissions.splice(index, 1);
      //Remove from Multiselect
      const roleIndex = this.selectedRoles.findIndex(role => role == per.role);
      if (roleIndex >= 0) {
        this.selectedRoles.splice(roleIndex, 1);
      }
    } else {
      console.warn("Permission not found");
    }
  }

  private initTableColumns(): void {
    this.roleTableColumns = [
      {field: 'role', header: 'Role'}
    ];
    this.actionTableColumns = [
      {field: 'role', header: 'Role', format: TableFormat.TitleCase},
      {field: 'canRead', header: 'Read', format: TableFormat.BooleanAsWritableInputSwitch},
      {field: 'canWrite', header: 'Write', format: TableFormat.BooleanAsWritableInputSwitch},
      {field: 'canDelete', header: 'Delete', format: TableFormat.BooleanAsWritableInputSwitch},
      {field: 'canPermit', header: 'Can Edit Permissions', format: TableFormat.BooleanAsWritableInputSwitch},
      {field: 'action', header: 'Action', template: this.actionTemplate}
    ];
    this.roleConfigTableColumns = [
      {field: 'transaction', header: 'Transaction', format: TableFormat.TitleCase},
      {field: 'entitySpecificRole', header: 'Role', format: TableFormat.TitleCase},
      {field: 'className', header: 'Class Name'},
    ];
  }

  private getFileSettings(): void {
    if (this.isDefaultSettings()) {
      this.getDefaultPermissionSettings();
    } else if (this.isFileSettings()) {
      this.getFileSettingPermissions();
    }
  }

  private isDefaultSettings(): boolean {
    return this.settingType == FilePermissionSettingType.DefaultPermission;
  }

  private isFileSettings(): boolean {
    return this.settingType == FilePermissionSettingType.FilePermission;
  }

  private resetValues(): void {
    this.filePermissions = [];
    this.selectedRoles = [];
  }

  private getDefaultPermissionSettings(): void {
    forkJoin([this.dataService.getDefaultFilePermissions(this.user.managingOrgId), this.dataService.getFileSecurityRoleConfig(this.user.managingOrgId, this.transaction)]).subscribe(res => {
      this.defaultSettingsFromBackEnd = plainToClass(DefaultPermissionSetting, res[0].list);
      this.roleConfigForSelectedTxn = plainToClass(FileSecurityRoleConfig, res[1].list);
      this.processDefaultFileSettings();
    });
  }

  private getFileSettingPermissions(): void {
    this.docService.getFilePermissions(this.selectedDocFileId).subscribe(res => {
      this.existingFileSettings = plainToClass(FilePermissionSetting, res.list);
      this.processFileAccessSettings();
    });
  }

  private processDefaultFileSettings(): void {
    //Filter settings for selected transaction
    this.defaultSettingsForSelectedTxn = classToClass(this.defaultSettingsFromBackEnd.filter(setting => setting.transaction == this.transaction));
    //Populate existing permissions in the table
    this.resetValues();
    this.defaultSettingsForSelectedTxn.forEach(setting => {
      this.filePermissions.push(setting.permission);
      this.defaultPermissionMap.set(setting.permission.role, setting);
      this.selectedRoles.push(setting.permission.role);
      //TODO: Disable already present roles in multi select, if not managing master org
    });
  }

  private processFileAccessSettings(): void {
    this.resetValues();
    this.existingFileSettings.forEach(setting => {
      this.filePermissions.push(setting.permission);
      this.filePermissionMap.set(setting.permission.role, setting);
      this.selectedRoles.push(setting.permission.role);
    });
  }

  private getRoles(): void {
    this.commonDataService.getEnumPairAsDropdown(EnumType.RoleType, false).subscribe(res => {
      this.roles = res;
    });
  }

  private saveDefaultSettings(): void {
    const settingsToSave = this.formDefaultPermissionForSelectedPermissions(this.filePermissions);
    this.dataService.saveDefaultFilePermissions(settingsToSave, this.user.managingOrgId).subscribe(res => {
      this.defaultSettingsFromBackEnd = plainToClass(DefaultPermissionSetting, res.list);
      this.displayDialog = false;
      this.savePermissionEvent.emit(this.defaultSettingsFromBackEnd);
    });
  }

  private saveFileSettings(): void {
    const settingsToSave = this.formFileAccessSettingsForSelectedPermissions(this.filePermissions);
    this.docService.updateFilePermissions(this.selectedDocFileId, settingsToSave).subscribe(res => {
      this.existingFileSettings = plainToClass(FilePermissionSetting, res.list);
      this.displayDialog = false;
      //TODO: Handle a common emit event for default and file settings
      this.savePermissionEvent.emit(this.existingFileSettings);
    });
  }

  private formDefaultPermissionForSelectedPermissions(permissions: FilePermission[]): DefaultPermissionSetting[] {
    //Remove other transaction settings
    let defaultSettingsToSave: DefaultPermissionSetting[] = this.defaultSettingsFromBackEnd.filter(set => set.transaction != this.transaction);

    //For all selected permissions, check if it is an update or a new permission
    permissions.forEach(per => {
      if (this.defaultPermissionMap.has(per.role)) {
        let defaultSetting = this.defaultPermissionMap.get(per.role);
        defaultSetting.permission = per;
        defaultSettingsToSave.push(defaultSetting);
      } else {
        //Create a new Default Settings object
        let defaultSetting = new DefaultPermissionSetting();
        defaultSetting.permission = per;
        defaultSetting.transaction = this.transaction;
        defaultSettingsToSave.push(defaultSetting);
      }
    });
    return defaultSettingsToSave;
  }

  private formFileAccessSettingsForSelectedPermissions(permissions: FilePermission[]): FilePermissionSetting[] {

    let defaultSettingsToSave: FilePermissionSetting[] = [];
    permissions.forEach(per => {
      if (this.filePermissionMap.has(per.role)) {
        let defaultSetting = this.filePermissionMap.get(per.role);
        defaultSetting.permission = per;
        defaultSettingsToSave.push(defaultSetting);
      } else {
        //Create a new Default Settings object
        let defaultSetting = new FilePermissionSetting();
        defaultSetting.permission = per;
        defaultSetting.docFileId = this.selectedDocFileId;
        defaultSettingsToSave.push(defaultSetting);
      }
    });
    return defaultSettingsToSave;
  }
}
